import isNil from 'lodash/isNil';
import queryString from 'query-string';
import React, { useEffect } from 'react';
import { compose } from 'recompose';
import { Redirect, RouteComponentProps, withRouter } from 'react-router';
import { withAuthenticationViaGraphQL } from '../Auth';
import { logException } from '../logging';
import RoutePath from '../RoutePath';
import { RouteConfig } from '../routes';
import RouteWithSubRoute from './RouteWithSubRoute';
import {
  withConfirmEmailUpdate,
  WithConfirmEmailUpdateProps,
} from './withConfirmEmailUpdate';

type UseConfirmEmailUpdateProps = Pick<
  RedirectIfAuthenticatedProps,
  'location' | 'history' | 'confirmEmailUpdate'
>;

const isSafeRedirectTarget = (
  value: string | null | undefined,
): value is string => {
  if (!value) return false;
  if (value[0] !== '/') return false;
  if (value[1] === '/' || value[1] === '\\') return false;
  if (value.includes('\\')) return false;

  return true;
};

const useConfirmEmailUpdate = ({
  location,
  history,
  confirmEmailUpdate,
}: UseConfirmEmailUpdateProps) => {
  useEffect(() => {
    const confirmEmailUpdateIfNeeded = async () => {
      const { search } = location;
      const query = queryString.parse(search);
      const { emailToken } = query;

      if (
        !isNil(emailToken) &&
        !Array.isArray(emailToken) &&
        emailToken.length > 0
      ) {
        const response = await confirmEmailUpdate({ emailToken });

        const confirmEmailUpdateSuccess = Boolean(response.success);

        history.push(RoutePath.Account, {
          confirmEmailUpdateSuccess,
          confirmEmailUpdateError: !confirmEmailUpdateSuccess,
        });
      }
    };

    confirmEmailUpdateIfNeeded().catch(e => {
      logException(e);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

interface RedirectIfAuthenticatedProps
  extends RouteConfig, WithConfirmEmailUpdateProps, React.Attributes {
  authenticated: boolean;
  authenticating: boolean;
  redirectIfAuthenticated: string;
  location: RouteComponentProps['location'];
  history: RouteComponentProps['history'];
  renderIfAuthenticating: React.ComponentType<RouteConfig>;
}

export const RedirectIfAuthenticated: React.FC<
  RedirectIfAuthenticatedProps
> = ({
  authenticated,
  authenticating,
  redirectIfAuthenticated,
  renderIfAuthenticating: Authenticating,
  location,
  history,
  confirmEmailUpdate,
  ...props
}) => {
  useConfirmEmailUpdate({ location, history, confirmEmailUpdate });
  if (authenticating) {
    return <Authenticating {...props} />;
  }
  if (authenticated) {
    const redirectParam = new URLSearchParams(location.search).get('redirect');
    const target = isSafeRedirectTarget(redirectParam)
      ? redirectParam
      : redirectIfAuthenticated;

    return <Redirect to={target} />;
  }

  return <RouteWithSubRoute {...props} />;
};

export default compose<
  RedirectIfAuthenticatedProps,
  Pick<RedirectIfAuthenticatedProps, 'renderIfAuthenticating'>
>(
  withAuthenticationViaGraphQL,
  withConfirmEmailUpdate,
  withRouter,
)(RedirectIfAuthenticated);
